import {
	createDirectus,
	readItems,
	readItem,
	readSingleton,
	rest,
	readUser,
	createItem,
	uploadFiles,
	withToken,
} from '@directus/sdk';
import type { RestClient } from '@directus/sdk';
import Queue from 'p-queue';
import type { Schema } from '@/types/directus-schema';
import { logger } from '../logger';
import { serializeError } from '../serialize-error';

// Helper for retrying fetch requests
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const fetchRetry = async (count: number, ...args: Parameters<typeof fetch>) => {
	const [url, options] = args;

	// Determine if the request is using the public token or a privileged token
	// The Directus SDK may pass headers as a Headers instance or a plain object
	let authHeader: string | null = null;
	if (options?.headers instanceof Headers) {
		authHeader = options.headers.get('Authorization') || options.headers.get('authorization');
	} else if (options?.headers) {
		const h = options.headers as Record<string, string>;
		authHeader = h['Authorization'] || h['authorization'] || null;
	}

	const publicToken = process.env.DIRECTUS_PUBLIC_TOKEN || '';

	// If we are using a specific token that is not the public one, we bypass the cache
	// This ensures that previews and visual editing always show the latest data
	// When publicToken is empty/unset, treat any authenticated request as privileged
	const isPrivilegedRequest = authHeader
		? publicToken.length > 0
			? !authHeader.includes(publicToken)
			: true // No public token configured → any auth = privileged
		: false;

	if (isPrivilegedRequest) {
		logger.info('Privileged Directus request (cache bypassed)', {
			url: typeof url === 'string' ? url.split('?')[0] : 'unknown',
		});
	}

	// Add Next.js caching options to all requests
	const enhancedOptions = {
		...options,
		next: {
			revalidate: isPrivilegedRequest ? 0 : 60, // Default to 1 minute cache, 0 for privileged
			tags: ['directus'], // Allow invalidating all directus content at once
		},
		cache: (isPrivilegedRequest ? 'no-store' : undefined) as RequestCache | undefined,
	};

	try {
		const response = await fetch(url, enhancedOptions);

		if (count > 2 || response.status !== 429) return response;

		logger.warn(`Directus 429 Too Many Requests`, { attempt: count + 1 });

		await sleep(500);

		return fetchRetry(count + 1, url, enhancedOptions);
	} catch (error) {
		logger.warn(`Directus fetch failed`, serializeError(error));

		// Return a mock response with an empty JSON object/array to prevent crashes
		return new Response(JSON.stringify({ data: [] }), {
			status: 200,
			headers: { 'Content-Type': 'application/json' },
		});
	}
};

// Queue for rate-limited requests
const queue = new Queue({ intervalCap: 10, interval: 500, carryoverConcurrencyCount: true });

const directusUrl = process.env.NEXT_PUBLIC_DIRECTUS_URL || 'http://localhost:8055';

if (!process.env.NEXT_PUBLIC_DIRECTUS_URL) {
	logger.warn('NEXT_PUBLIC_DIRECTUS_URL is not set, using default: http://localhost:8055');
}

const directus = createDirectus<Schema>(directusUrl, {
	globals: {
		fetch: (...args) => queue.add(() => fetchRetry(0, ...args)),
	},
}).with(rest());

export const useDirectus = () => ({
	directus: directus as RestClient<Schema>,
	readItems,
	readItem,
	readSingleton,
	readUser,
	createItem,
	uploadFiles,
	withToken,
});

export const getDirectus = useDirectus;
